/**
 * metrics-diagnosis-adapter.ts — 数据中台诊断下钻「真 API DTO → 页面消费形状」塑形
 *
 * GET /api/v1/metrics/diagnosis 返回 snake_case DTO（服务端 DTO interface = packages/server
 * diagnosis-metrics-read-service.ts 的 DiagnosisData）。本 adapter 做：
 * - 收入派生指标（revenue_d7/ltv_d30）的 PENDING_INSTRUMENTATION 占位对象
 *   → `{ value: null, pending: true }` 单元（页面灰显、禁显假数字）；
 * - 金额 minor string → number（USD minor units，页面用 fmtUsdMinor 格式化）；
 * - dimensions 就绪度块透传（hour/book 恒 pending —— 页面据此渲染禁用维度按钮 + 原因）；
 * - hour_bucket/book_id 恒 null（无源列）不进视图模型（页面无对应列）。
 * 契约缺字段落 null / pending，不编造数据。
 */

import { isPendingInstrumentation } from '@novel-admin/shared';

import type { FinancialCoverage } from '@novel-admin/shared/schemas/financial-coverage';

// ---------- DTO（snake_case，镜像服务端 DiagnosisData；shared 收录随契约演进跟进） ----------

type Pending = { status: 'PENDING_INSTRUMENTATION' };

/** 维度就绪度（镜像服务端 DiagnosisDimensionStatus）：ready 可下钻 / pending 无源 */
export type DiagnosisDimensionStatus = 'ready' | 'pending';

export interface DiagnosisRowDto {
  /** media_source（''=自然量，维度键与 user_attributions 对齐） */
  channel: string;
  /** 恒 null（无源：两表 date 粒度无小时列） */
  hour_bucket: null;
  /** 恒 null（无源：user_attributions 无 book 列，禁 alter C 端表） */
  book_id: null;
  /** live：Σcohort_size（user_attributions 归因口径） */
  installs: number;
  revenue_d7_usd_minor: string | null | Pending;
  ltv_d30_usd_minor: string | null | Pending;
}

export interface MetricsDiagnosisResponse {
  financial_coverage?: FinancialCoverage;
  app_scope: string;
  currency: 'USD';
  /** range=null 表示自定义 from/to 窗（非 range 派生） */
  window: { range: string | null; from: string; to: string };
  computed_at: string | null;
  data_freshness: { txn: string; behavioral: string };
  /** 维度就绪度（前端据此渲染维度按钮可用性 / pending 徽标） */
  dimensions: {
    channel: DiagnosisDimensionStatus;
    hour: DiagnosisDimensionStatus;
    book: DiagnosisDimensionStatus;
  };
  /** 回显过滤器（hour/book 无源，非空在 route 层即 422 → 恒空数组） */
  filters: {
    channel: string[];
    campaign_id: string[];
    hour_bucket: string[];
    book_id: string[];
  };
  /** 顶层未就绪收入指标 keys（revenue_d7/ltv_d30 子集；就绪 → []） */
  pending: string[];
  rows: DiagnosisRowDto[];
}

// ---------- 视图模型（camelCase，页面消费） ----------

/** 派生指标单元：pending=true → 灰显（PENDING 占位，禁示例值）；value=null → 就绪但无法派生（—） */
export interface DerivedMetricCell {
  value: number | null;
  pending: boolean;
}

export interface DiagnosisRowView {
  /** media_source（''=自然量，页面映射展示名） */
  channel: string;
  installs: number;
  /** 收入派生（就绪闸控）：D7 累计收入（minor units） */
  revenueD7Minor: DerivedMetricCell;
  /** 收入派生（就绪闸控）：realized D30 人均 LTV（minor units） */
  ltvD30Minor: DerivedMetricCell;
}

export interface MetricsDiagnosisViewData {
  financialCoverage?: FinancialCoverage;
  computedAt: string | null;
  window: { range: string | null; from: string; to: string };
  dimensions: {
    channel: DiagnosisDimensionStatus;
    hour: DiagnosisDimensionStatus;
    book: DiagnosisDimensionStatus;
  };
  /** 顶层未就绪收入指标 keys —— 表头 PendTag 显隐依据 */
  pending: string[];
  rows: DiagnosisRowView[];
}

/** minor string|null → number|null（非法 → null） */
function minorOrNull(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** minor string|null|PENDING → 派生指标单元（minor units number） */
function minorCell(v: string | null | Pending | undefined): DerivedMetricCell {
  if (isPendingInstrumentation(v)) return { value: null, pending: true };
  return { value: minorOrNull(v), pending: false };
}

export function adaptMetricsDiagnosis(dto: MetricsDiagnosisResponse): MetricsDiagnosisViewData {
  return {
    financialCoverage: dto.financial_coverage,
    computedAt: dto.computed_at,
    window: dto.window,
    dimensions: {
      channel: dto.dimensions.channel,
      hour: dto.dimensions.hour,
      book: dto.dimensions.book,
    },
    pending: dto.pending,
    rows: dto.rows.map((r) => ({
      channel: r.channel,
      installs: r.installs,
      revenueD7Minor:
        dto.financial_coverage?.usd_ready === true
          ? minorCell(r.revenue_d7_usd_minor)
          : { value: null, pending: true },
      ltvD30Minor:
        dto.financial_coverage?.usd_ready === true
          ? minorCell(r.ltv_d30_usd_minor)
          : { value: null, pending: true },
    })),
  };
}
